import { useState } from "react";
import { Controller, useController, useFormContext } from "react-hook-form";
import { FIELD_REQUIRED, FORM } from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getReactHookFormNestedError, LaunchError } from "../../utils/errors";
import _ from "lodash";
import ModalLeadFormsList from "./ModalLeadFormsList";
import LeadFormSummaryCard from "../LeadForm/LeadFormSummaryCard";
import ModalViewLeadForm from "./ModalViewLeadForm";
import ModalCreateLeadForm from "./ModalCreateLeadForm";
import InstantFormCardActions from "../LeadForm/InstantFormCardActions";
import { LINKEDIN } from "../../utils/constants";
import {
  applyCreatedLeadFormFromCard,
  collectOpenDraftFormTargets,
  prepareRecommendedLeadFormCreate,
  readRecommendedLeadForm,
  toSelectedInstantFormValue,
} from "../../utils/leadFormInitialValues";
import { stampRecommendedLeadFormTargets } from "../../utils/aiGeneratedForm";
import { mergeLeadFormPreviewStorageFields } from "../../utils/leadFormPreview";

const LeadFormContainer = ({
  name,
  integrationID,
  adAccountID,
  control,
  errors = {},
  disabled,
}) => {
  const {
    field: { value, onChange },
  } = useController({
    control,
    name,
    defaultValue: "",
  });
  const [modalTemplateOpen, setModalTemplateOpen] = useState(false);
  const [modalViewOpen, setModalViewOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createInitialValues, setCreateInitialValues] = useState(null);
  const [listCreateInitialValues, setListCreateInitialValues] = useState(null);
  const [recommendedAdIDs, setRecommendedAdIDs] = useState(null);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { watch, sectionSelected, getValues, setValue, campaignID } =
    useFormContext();
  const campaignLanguage = watch(`platformsData.${LINKEDIN}.languages`);
  const recommendations = watch(`platformsData.${LINKEDIN}.recommendations`);
  const advertiserID = useSelectorApp(
    (state) => state.auth?.user?.advertiserID
  );
  const leadFormRecommendation = {
    adID: sectionSelected?.assetsGroupID,
    adGroupID: sectionSelected?.assetsGroupID,
    campaignID,
    advertiserID,
    language: campaignLanguage,
    getCampaignValues: getValues,
    setCampaignValue: setValue,
  };
  const isEmptyTemplate = _.isEmpty(value);

  const onPrepareCreate = () =>
    prepareRecommendedLeadFormCreate({
      platform: LINKEDIN,
      recommendations,
      entityID: sectionSelected?.assetsGroupID,
      dispatch,
      launchError: LaunchError,
    });

  const onToggleModalTemplate = () => {
    if (disabled) return;
    if (!modalTemplateOpen) {
      setListCreateInitialValues(onPrepareCreate().initialValues);
    }
    setModalTemplateOpen((prev) => !prev);
  };

  const onApplyLeadForm = (leadForm) => {
    const leadFormValue = toSelectedInstantFormValue({
      platform: LINKEDIN,
      form: leadForm,
    });
    if (!leadFormValue) return;
    onChange(leadFormValue);
  };

  const recommendedLeadForm = readRecommendedLeadForm({
    platform: LINKEDIN,
    recommendations,
    entityID: sectionSelected?.assetsGroupID,
  });

  const onOpenCreateModal = () => {
    if (disabled) return;
    const prepared = onPrepareCreate();
    setCreateInitialValues(prepared.initialValues);
    setRecommendedAdIDs(prepared.adIDs);
    setCreateModalOpen(true);
  };

  const onCloseCreateModal = () => {
    setCreateModalOpen(false);
    setCreateInitialValues(null);
    setRecommendedAdIDs(null);
  };

  const onSaveCreatedLeadForm = (leadForm) => {
    let leadFormValue = toSelectedInstantFormValue({
      platform: LINKEDIN,
      form: leadForm,
    });
    if (!leadFormValue) return;
    // The editor may have generated and stored a recommendation after it
    // opened with defaults; it is assigned like one prepared on open.
    const prepared = createInitialValues
      ? { initialValues: createInitialValues, adIDs: recommendedAdIDs }
      : onPrepareCreate();
    const stampTargets = prepared.initialValues
      ? collectOpenDraftFormTargets({
          platform: LINKEDIN,
          adsGroups: getValues?.("adsGroups"),
          assetsGroups: getValues?.("assetsGroups"),
          adIDs: prepared.adIDs,
          currentAdID: sectionSelected?.assetsGroupID,
        })
      : [];
    if (prepared.initialValues) {
      leadFormValue = {
        ...leadFormValue,
        ...mergeLeadFormPreviewStorageFields(
          LINKEDIN,
          leadForm,
          prepared.initialValues
        ),
      };
    }
    applyCreatedLeadFormFromCard({
      platform: LINKEDIN,
      formValue: leadFormValue,
      hasRecommendation: Boolean(prepared.initialValues),
      adIDs: prepared.adIDs,
      currentAdID: sectionSelected?.assetsGroupID,
      getValues,
      setValue,
      onChange,
    });
    if (stampTargets.length > 0) {
      stampRecommendedLeadFormTargets({
        getValues,
        setValue,
        targets: stampTargets,
      });
    }
  };

  const onOpenViewLeadForm = () => {
    setModalViewOpen(true);
  };

  const onCloseViewLeadForm = () => {
    setModalViewOpen(false);
  };

  const showActions = isEmptyTemplate || !disabled;

  return (
    <Controller
      name={name}
      id={name}
      control={control}
      rules={{
        required: {
          value: true,
          message: t(FIELD_REQUIRED),
        },
      }}
      render={() => (
        <Box id={name}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {!isEmptyTemplate && (
              <Box
                sx={{
                  width: "100%",
                }}
              >
                {value?.id ? (
                  <LeadFormSummaryCard
                    platform={LINKEDIN}
                    form={value}
                    recommendation={recommendedLeadForm}
                    integrationID={integrationID}
                    adAccountID={adAccountID}
                    onView={onOpenViewLeadForm}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                  >
                    {`${t(FORM)}: ${value.name}`}
                  </Typography>
                )}
              </Box>
            )}
            {showActions && (
              <InstantFormCardActions
                disabled={disabled}
                hasSelection={!isEmptyTemplate}
                onSelect={onToggleModalTemplate}
                onCreate={onOpenCreateModal}
                marginTop={1}
              />
            )}
          </Box>
          {error?.message && (
            <Typography
              variant="body2"
              color="error"
              sx={{
                mt: 1,
              }}
            >
              {error.message}
            </Typography>
          )}
          {modalTemplateOpen && (
            <ModalLeadFormsList
              modalOpen={modalTemplateOpen}
              onCloseModal={onToggleModalTemplate}
              integrationID={integrationID}
              adAccountID={adAccountID}
              campaignLanguage={campaignLanguage}
              createInitialValues={listCreateInitialValues}
              leadFormRecommendation={leadFormRecommendation}
              onCallbackSelect={onApplyLeadForm}
            />
          )}
          {createModalOpen && (
            <ModalCreateLeadForm
              modalOpen={createModalOpen}
              onCloseModal={onCloseCreateModal}
              integrationID={integrationID}
              adAccountID={adAccountID}
              campaignLanguage={campaignLanguage}
              initialValues={createInitialValues}
              leadFormRecommendation={leadFormRecommendation}
              aiGeneratedDraft={Boolean(createInitialValues)}
              onCallbackSave={onSaveCreatedLeadForm}
            />
          )}
          {modalViewOpen && (
            <ModalViewLeadForm
              modalOpen
              onCloseModal={onCloseViewLeadForm}
              onCallbackSave={onApplyLeadForm}
              leadForm={value}
              integrationID={integrationID}
              adAccountID={adAccountID}
              campaignLanguage={campaignLanguage}
            />
          )}
        </Box>
      )}
    />
  );
};

export default LeadFormContainer;
